import { useState, useEffect, useMemo } from 'react';
import { fetchClaims, createCreditBalance } from '../services/api';
import './ChangePayerModal.css';

const SOURCES = [
  { value: 'duplicate_payment', label: 'Duplicate payment' },
  { value: 'overpayment_era',   label: 'Payer overpayment (ERA)' },
  { value: 'cob_correction',    label: 'COB correction' },
  { value: 'manual',            label: 'Other / manual' },
];

/**
 * Manually log a credit balance (overpayment owed back to a payer) against a claim —
 * for duplicate payments, payer overpayments, or patient overpayments that the
 * automatic rebill-after-payment flow doesn't capture.
 */
export default function CreditBalanceModal({ onClose, onSaved }) {
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);

  const [claimSearch, setClaimSearch] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedClaim, setSelectedClaim] = useState(null);

  const [payerName, setPayerName] = useState('');
  const [amount, setAmount]       = useState('');
  const [source, setSource]       = useState('duplicate_payment');
  const [notes, setNotes]         = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [dupWarning, setDupWarning] = useState(null); // existing-credit-balance confirmation

  useEffect(() => {
    let active = true;
    fetchClaims()
      .then((res) => { if (active) setClaims(res.data || []); })
      .catch(() => active && setError('Failed to load claims.'))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, []);

  const filteredClaims = useMemo(() => {
    const q = claimSearch.trim().toLowerCase();
    if (!q) return claims.slice(0, 8);
    return claims
      .filter((c) => c.claim_number?.toLowerCase().includes(q) || c.patient_name?.toLowerCase().includes(q))
      .slice(0, 8);
  }, [claims, claimSearch]);

  const canSubmit = selectedClaim && payerName.trim() && parseFloat(amount) > 0 && !submitting;

  const submit = async (confirmDuplicate = false) => {
    setSubmitting(true);
    setError(null);
    try {
      await createCreditBalance({
        claim_id:      selectedClaim.id,
        payer_name:    payerName.trim(),
        credit_amount: parseFloat(amount),
        source,
        notes:         notes.trim() || null,
        confirm_duplicate: confirmDuplicate,
      });
      onSaved?.();
    } catch (err) {
      // Non-blocking duplicate guard: this claim already has an unresolved credit balance.
      if (err?.response?.data?.requires_confirmation === 'duplicate_credit_balance') {
        setDupWarning(err.response.data.error);
      } else {
        setError(err?.response?.data?.error || 'Failed to log the credit balance.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="cpm-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget && !submitting) onClose(); }}>
      <div className="cpm-modal">
        <div className="cpm-header">
          <div>
            <h2 className="cpm-title">Log Credit Balance</h2>
            <p className="cpm-subtitle">Record an overpayment owed back to a payer</p>
          </div>
          <button className="cpm-close" onClick={onClose} disabled={submitting}>×</button>
        </div>

        <div className="cpm-body">
          {/* Claim picker */}
          <label className="cpm-field-label">Claim</label>
          {selectedClaim ? (
            <div className="cpm-payer-selected">
              <span>{selectedClaim.claim_number} · {selectedClaim.patient_name}</span>
              <button onClick={() => { setSelectedClaim(null); setClaimSearch(''); }}>Change</button>
            </div>
          ) : (
            <div className="cpm-combobox">
              <input
                className="cpm-input"
                placeholder={loading ? 'Loading claims…' : 'Search claim # or patient…'}
                value={claimSearch}
                disabled={loading}
                onChange={(e) => { setClaimSearch(e.target.value); setDropdownOpen(true); }}
                onFocus={() => setDropdownOpen(true)}
                onBlur={() => setTimeout(() => setDropdownOpen(false), 150)}
              />
              {dropdownOpen && filteredClaims.length > 0 && (
                <div className="cpm-dropdown">
                  {filteredClaims.map((c) => (
                    <button
                      key={c.id}
                      className="cpm-dropdown-item"
                      onMouseDown={() => {
                        setSelectedClaim(c);
                        setDropdownOpen(false);
                        // Auto-fill the payer owed from the claim's primary payer (still editable).
                        if (c.payer_name) setPayerName(c.payer_name);
                      }}
                    >
                      {c.claim_number}
                      <span className="cpm-dropdown-code">{c.patient_name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="cpm-field-row">
            <div className="cpm-field">
              <label className="cpm-field-label">Payer owed</label>
              <input className="cpm-input" value={payerName} onChange={(e) => setPayerName(e.target.value)} placeholder="e.g. Aetna" />
            </div>
            <div className="cpm-field">
              <label className="cpm-field-label">Amount</label>
              <input className="cpm-input" type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" />
            </div>
          </div>

          <div className="cpm-field">
            <label className="cpm-field-label">Reason</label>
            <select className="cpm-input" value={source} onChange={(e) => setSource(e.target.value)}>
              {SOURCES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>

          <div className="cpm-field">
            <label className="cpm-field-label">Notes (optional)</label>
            <input className="cpm-input" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Context for the credit balance" />
          </div>

          <p className="cpm-hint">A 60-day compliance due date is set automatically from today.</p>

          {error && <p className="cpm-error">{error}</p>}

          {dupWarning && (
            <div className="cpm-warning">
              {dupWarning}
              <p>Confirm only if this is a separate overpayment from the existing one.</p>
            </div>
          )}

          <div className="cpm-actions">
            <button className="cpm-btn-secondary" onClick={onClose} disabled={submitting}>Cancel</button>
            {dupWarning ? (
              <button className="cpm-btn-primary" onClick={() => submit(true)} disabled={submitting}>
                {submitting ? 'Saving…' : 'Log anyway'}
              </button>
            ) : (
              <button className="cpm-btn-primary" onClick={() => submit(false)} disabled={!canSubmit}>
                {submitting ? 'Saving…' : 'Log credit balance'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
